import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { isValidTime } from "@src/packages/utils/time";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import {
  Pressable,
  ScrollView,
  View,
  Text,
  TextInput,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
  Alert,
} from "react-native";
import {
  X,
  Search,
  Camera,
  Zap,
  ChevronRight,
  ChevronDown,
  Clock,
  Minus,
  Pill,
  Syringe,
  Droplets,
  Hand,
  Sun,
  Sunset,
  Moon,
  CloudMoon,
} from "lucide-react-native";
import * as ImagePicker from "expo-image-picker";
import {
  projectDocumentProcessingRunToPrefill,
  useDocumentSourceTransfer,
  type DocumentProcessingPrefillMedication,
  type DocumentSourceFile,
} from "@src/packages/documents/ingestion";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";
import {
  deserializeMedicationRoute,
  serializeMedicationRoute,
  useCreateMedication,
  useUpdateMedication,
} from "@src/packages/care";
import {
  AnchoredSelectMenu,
  Dialog,
  DialogPrimaryButton,
  MoonFilterChip,
  MoonToggle,
  MoonSearchInput,
  MoonFormField,
  MoonDateField,
  SoftSecondaryButton,
  Tag,
  type AnchoredSelectMenuItem,
  type TagTone,
} from "@louise/moon-ui";
import {
  useMedications,
  findMedicationByName,
} from "@src/packages/care/hooks/useMedications";
import {
  resolveTriggerShotPhase,
  buildTriggerShotPatch,
} from "@src/packages/care/trigger-shot-phase";
import {
  usePhases,
  useUpdatePhase,
} from "@src/packages/patients/hooks/usePhases";
import type { Medication as DbMedication } from "@src/packages/care/hooks/useMedications";
import { useRxNormSearch, parseDrugName } from "@src/packages/medications";
import { parseDoseInput } from "@src/packages/schemas";
import type { RxNormSearchResult } from "@src/packages/medications";
import {
  getBlocksByKind,
  getMedicationByBrandName,
} from "@src/packages/content";
import type {
  ContentBlock,
  MedicationBody,
  SupplementBody,
} from "@src/packages/content";
import { getTodayString as getToday } from "@src/packages/utils/date";
import { useTimezone } from "@src/packages/preferences";
import { EMBER } from "../../../../onboarding/theme";
import { useTranslations } from "@src/packages/i18n";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// ============================================================================
// Types
// ============================================================================

/** Shape accepted for editing an existing medication */
export type EditMedicationData = {
  id: string;
  name: string;
  dose?: number | null;
  doseUnit?: string | null;
  /** Persisted patient-medication administration route, not delivery form. */
  route?: string | null;
  /** API medication form value (e.g., "pre_filled_pen", "injection_solution") */
  form?: string | null;
  timeTaken?: string[];
  startDate?: string;
  endDate?: string | null;
  notes?: string | null;
};

export type AddMedicationInventoryForm =
  "pen" | "vial" | "pill" | "suppository" | "supplies";

export type AddMedicationInventoryEntry = {
  name: string;
  dosage: string;
  doseAmount: number | null;
  doseUnit: string | null;
  route: string | null;
  routeLabel: string;
  deliveryMethod: string | null;
  deliveryMethodLabel: string | null;
  form: AddMedicationInventoryForm;
  quantity: number;
  unitLabel: string;
  medicationId: string | null;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  cycleId?: string | null;
  /** Initial start date when the flow is launched from a selected calendar day. */
  initialStartDate?: string;
  /** Restricts historical backfill to the selected cycle's date window. */
  cycleDateBounds?: {
    startDate: string;
    endDate?: string | null;
  };
  phaseId?: string | null;
  /** Called when trigger shot flow wants to open AddAppointmentModal pre-filled */
  onRequestRetrievalAppointment?: (prefill: {
    type: string;
    subType: string;
    suggestedDate: string;
    suggestedTime: string;
  }) => void;
  /** Explicit medication role override (from treatment hub) */
  medicationRole?: "standard" | "trigger_shot" | null;
  /** When provided, opens in edit mode at the Configure step with pre-filled data */
  editMedication?: EditMedicationData | null;
  /** Inventory mode keeps the standard search/configure flow but adds stock-on-hand fields. */
  inventoryMode?: boolean;
  onInventoryMedicationAdded?: (
    entry: AddMedicationInventoryEntry,
  ) => void | Promise<void>;
  /** Fires after a successful create — orchestrators (e.g., the protocol-
   * adjustment swap/add flow) use this to write an audit-trail note. */
  onMedicationCreated?: (info: {
    name: string;
    dose: number | null;
    doseUnit: string | null;
  }) => void;
  /** Fires after a successful edit-mode update with both the new and prior
   * dose values, so callers can record dose changes. */
  onMedicationUpdated?: (info: {
    id: string;
    name: string;
    dose: number | null;
    doseUnit: string | null;
    previous: { dose: number | null; doseUnit: string | null };
  }) => void;
  /** Small banner shown at the top of the Configure step — e.g. "Replacing
   * Gonal-F" when this modal is opened as the second step of a swap flow. */
  replacingMedicationName?: string;
};

type Step = 0 | 1 | 2 | 3;

type TimePreset = {
  labelKey: string;
  value: string;
  icon: typeof Sun;
};

const TIME_PRESETS: TimePreset[] = [
  { labelKey: "add_medication.time_preset.morning", value: "08:00", icon: Sun },
  {
    labelKey: "add_medication.time_preset.afternoon",
    value: "12:00",
    icon: Sunset,
  },
  {
    labelKey: "add_medication.time_preset.evening",
    value: "18:00",
    icon: Moon,
  },
  {
    labelKey: "add_medication.time_preset.night",
    value: "21:00",
    icon: CloudMoon,
  },
];

const DOSE_UNITS = [
  "mg",
  "IU",
  "mcg",
  "ml",
  "g",
  "units",
  "pills",
  "tablets",
  "capsules",
  "drops",
  "patches",
];

const DOSE_UNIT_KEYS: Record<string, string> = {
  units: "add_medication.dose_unit.units",
  pills: "add_medication.dose_unit.pills",
  tablets: "add_medication.dose_unit.tablets",
  capsules: "add_medication.dose_unit.capsules",
  drops: "add_medication.dose_unit.drops",
  patches: "add_medication.dose_unit.patches",
};

type RouteOption = { labelKey: string; value: string; icon: typeof Pill };

const ROUTE_OPTIONS: RouteOption[] = [
  { labelKey: "add_medication.route_option.oral", value: "oral", icon: Pill },
  {
    labelKey: "add_medication.route_option.subcutaneous",
    value: "subcutaneous",
    icon: Syringe,
  },
  {
    labelKey: "add_medication.route_option.intramuscular",
    value: "intramuscular",
    icon: Syringe,
  },
  {
    labelKey: "add_medication.route_option.vaginal",
    value: "vaginal",
    icon: Droplets,
  },
  {
    labelKey: "add_medication.route_option.topical",
    value: "topical",
    icon: Hand,
  },
  { labelKey: "add_medication.route_option.patch", value: "patch", icon: Hand },
];
const ROUTES = ROUTE_OPTIONS.map((r) => r.value);

const INJECTABLE_ROUTES = ["subcutaneous", "intramuscular"];

type DeliveryMethodOption = { labelKey: string; value: string };

const INJECTION_DELIVERY_METHODS: DeliveryMethodOption[] = [
  {
    labelKey: "add_medication.delivery_method_option.pre_filled_syringe",
    value: "pre_filled_syringe",
  },
  {
    labelKey: "add_medication.delivery_method_option.pre_filled_pen",
    value: "pre_filled_pen",
  },
  {
    labelKey: "add_medication.delivery_method_option.injection_solution",
    value: "injection_solution",
  },
  {
    labelKey: "add_medication.delivery_method_option.lyophilized_powder",
    value: "lyophilized_powder",
  },
  {
    labelKey: "add_medication.delivery_method_option.multi_vial",
    value: "multi_vial",
  },
];

/** Maps content MedicationForm values to API form values */
const CONTENT_FORM_TO_API_FORM: Record<string, string> = {
  pen: "pre_filled_pen",
  vial: "injection_solution",
};

/** Maps API form value to the value we send (multi_vial → other) */
function mapDeliveryMethodToApiForm(value: string): string {
  if (value === "multi_vial") return "other";
  return value;
}

type DosagePreset = { doses: number[]; unit: string; route: string };

/** Quick-select dosage presets + known route by medication name (case-insensitive) */
const DOSAGE_PRESETS: Record<string, DosagePreset> = {
  "gonal-f": {
    doses: [75, 150, 225, 300, 450, 900],
    unit: "IU",
    route: "subcutaneous",
  },
  follistim: {
    doses: [75, 150, 225, 300, 450, 900],
    unit: "IU",
    route: "subcutaneous",
  },
  menopur: { doses: [75, 150, 225], unit: "IU", route: "subcutaneous" },
  cetrotide: { doses: [0.25, 3], unit: "mg", route: "subcutaneous" },
  ganirelix: { doses: [0.25], unit: "mg", route: "subcutaneous" },
  letrozole: { doses: [2.5, 5, 7.5], unit: "mg", route: "oral" },
  femara: { doses: [2.5, 5, 7.5], unit: "mg", route: "oral" },
  clomid: { doses: [50, 100, 150], unit: "mg", route: "oral" },
  clomiphene: { doses: [50, 100, 150], unit: "mg", route: "oral" },
  progesterone: { doses: [50, 100, 200], unit: "mg", route: "intramuscular" },
  endometrin: { doses: [100, 200], unit: "mg", route: "vaginal" },
  prometrium: { doses: [100, 200, 400], unit: "mg", route: "oral" },
  crinone: { doses: [90], unit: "mg", route: "vaginal" },
  ovidrel: { doses: [250], unit: "mcg", route: "subcutaneous" },
  "choriogonadotropin alfa": {
    doses: [250],
    unit: "mcg",
    route: "subcutaneous",
  },
  pregnyl: { doses: [5000, 10000], unit: "IU", route: "intramuscular" },
  novarel: { doses: [5000, 10000], unit: "IU", route: "intramuscular" },
  hcg: { doses: [5000, 10000], unit: "IU", route: "intramuscular" },
  "chorionic gonadotropin": {
    doses: [5000, 10000],
    unit: "IU",
    route: "intramuscular",
  },
  lupron: { doses: [0.25, 0.5, 1], unit: "mg", route: "subcutaneous" },
  leuprolide: { doses: [0.25, 0.5, 1], unit: "mg", route: "subcutaneous" },
  estrace: { doses: [1, 2, 4], unit: "mg", route: "oral" },
  estradiol: { doses: [1, 2, 4], unit: "mg", route: "oral" },
  dexamethasone: { doses: [0.5, 0.75, 1], unit: "mg", route: "oral" },
  prednisone: { doses: [5, 10, 20], unit: "mg", route: "oral" },
  metformin: { doses: [500, 850, 1000], unit: "mg", route: "oral" },

  // ─── French brand names (presets mirror French AMP practice) ───────
  décapeptyl: { doses: [0.1, 3, 11.25], unit: "mg", route: "subcutaneous" },
  decapeptyl: { doses: [0.1, 3, 11.25], unit: "mg", route: "subcutaneous" },
  ovitrelle: { doses: [250], unit: "mcg", route: "subcutaneous" },
  utrogestan: { doses: [100, 200], unit: "mg", route: "vaginal" },
  estima: { doses: [100, 200], unit: "mg", route: "vaginal" },
  progestan: { doses: [100, 200], unit: "mg", route: "vaginal" },
  provames: { doses: [1, 2], unit: "mg", route: "oral" },
  oromone: { doses: [1, 2], unit: "mg", route: "oral" },
  oestrofem: { doses: [1, 2], unit: "mg", route: "oral" },
  vivelledot: { doses: [25, 37.5, 50, 75, 100], unit: "mcg", route: "patch" },
  estrapatch: { doses: [40, 60, 80], unit: "mcg", route: "patch" },
  femsept: { doses: [50, 75, 100], unit: "mcg", route: "patch" },
  oesclim: { doses: [25, 50], unit: "mcg", route: "patch" },
  fostimon: { doses: [75, 150, 225], unit: "IU", route: "subcutaneous" },
  meriofert: { doses: [75, 150], unit: "IU", route: "subcutaneous" },
  ovaleap: {
    doses: [75, 150, 225, 300, 450, 900],
    unit: "IU",
    route: "subcutaneous",
  },
  bemfola: {
    doses: [75, 150, 225, 300, 450],
    unit: "IU",
    route: "subcutaneous",
  },
  pergotime: { doses: [50, 100, 150], unit: "mg", route: "oral" },
  enantone: { doses: [3.75, 11.25], unit: "mg", route: "intramuscular" },
  lovenox: { doses: [20, 40, 60, 80, 100], unit: "mg", route: "subcutaneous" },
  aspégic: { doses: [75, 100, 250], unit: "mg", route: "oral" },
  kardégic: { doses: [75, 160], unit: "mg", route: "oral" },
  glucophage: { doses: [500, 850, 1000], unit: "mg", route: "oral" },
  dostinex: { doses: [0.5, 1], unit: "mg", route: "oral" },
  levothyrox: {
    doses: [25, 50, 75, 100, 125, 150, 200],
    unit: "mcg",
    route: "oral",
  },
  euthyrox: {
    doses: [25, 50, 75, 100, 125, 150, 200],
    unit: "mcg",
    route: "oral",
  },
  cortancyl: { doses: [5, 10, 20], unit: "mg", route: "oral" },
  solupred: { doses: [5, 20], unit: "mg", route: "oral" },
  médrol: { doses: [4, 16, 32], unit: "mg", route: "oral" },
  vibramycine: { doses: [100], unit: "mg", route: "oral" },
};

const TRIGGER_MEDICATION_SEARCH_FALLBACKS = [
  "Ovidrel",
  "Ovitrelle",
  "Pregnyl",
  "Novarel",
  "Lupron",
  "Leuprolide",
  "hCG",
  "Chorionic gonadotropin",
  "Gonapeptyl",
  "Decapeptyl",
  "Décapeptyl",
];

function normalizePresetLookup(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function getDosagePresetForMedicationName(
  medicationName: string,
): DosagePreset | null {
  const lower = medicationName.trim().toLowerCase();
  const exact = DOSAGE_PRESETS[lower];
  if (exact) return exact;

  const normalized = normalizePresetLookup(medicationName);
  if (normalized.includes("ovidrel") || normalized.includes("ovitrelle")) {
    return DOSAGE_PRESETS.ovidrel ?? null;
  }
  if (normalized.includes("choriogonadotropinalfa")) {
    return DOSAGE_PRESETS["choriogonadotropin alfa"] ?? null;
  }
  if (normalized.includes("pregnyl")) return DOSAGE_PRESETS.pregnyl ?? null;
  if (normalized.includes("novarel")) return DOSAGE_PRESETS.novarel ?? null;
  if (
    normalized.includes("hcg") ||
    normalized.includes("chorionicgonadotropin") ||
    normalized.includes("choriogonadotropin")
  ) {
    return DOSAGE_PRESETS.hcg ?? null;
  }
  if (normalized.includes("lupron") || normalized.includes("leuprolide")) {
    return DOSAGE_PRESETS.lupron ?? null;
  }
  if (normalized.includes("decapeptyl") || normalized.includes("gonapeptyl")) {
    return DOSAGE_PRESETS.decapeptyl ?? null;
  }

  return null;
}

type DurationPreset = {
  labelKey: string;
  days: number | null; // null = ongoing
};

const DURATION_PRESETS: DurationPreset[] = [
  // `days` = number of days the med is taken, inclusive of startDate.
  // "Once" = 1 day → endDate === startDate.
  // "7 days" = 7 days → endDate === startDate + 6.
  { labelKey: "add_medication.duration_preset.once", days: 1 },
  { labelKey: "add_medication.duration_preset.7_days", days: 7 },
  { labelKey: "add_medication.duration_preset.10_days", days: 10 },
  { labelKey: "add_medication.duration_preset.14_days", days: 14 },
  { labelKey: "add_medication.duration_preset.3_weeks", days: 21 },
  { labelKey: "add_medication.duration_preset.1_month", days: 30 },
  { labelKey: "add_medication.duration_preset.3_months", days: 90 },
  { labelKey: "add_medication.duration_preset.ongoing", days: null },
];

/** Unified search result type covering all 3 tiers */
type MedSearchResult = {
  id: string;
  name: string;
  subtitle: string;
  source: "db" | "rxnorm" | "content" | "preset";
  /** Backend catalog medication (for UUID linkage) */
  dbMed?: DbMedication;
  /** RxNorm result (for rxcui + auto-fill) */
  rxnormResult?: RxNormSearchResult;
  /** Content block (for educational data) */
  contentBlock?: ContentBlock<"medication"> | ContentBlock<"supplement">;
};

const SOURCE_BADGE_CONFIG: Record<
  MedSearchResult["source"],
  { label: string; tone: TagTone }
> = {
  rxnorm: { label: "FDA", tone: "sourceBlue" },
  db: { label: "Rx", tone: "sourcePurple" },
  content: { label: "Supplement", tone: "sourceGreen" },
  preset: { label: "Quick", tone: "gold" },
};

// Quick-add presets are country-aware. In France the dominant brand for
// progesterone is Utrogestan (not "Progesterone" as a free text), the trigger
// shot is Ovitrelle (not Ovidrel), and the GnRH agonist is Décapeptyl. Names
// here flow straight into the search field, so they need to match what
// `brandsByCountry` knows about for that country.
const QUICK_ADD_MEDS_BY_COUNTRY: Record<string, string[]> = {
  US: ["Gonal-F", "Menopur", "Cetrotide", "Letrozole", "Progesterone"],
  FR: ["Gonal-F", "Décapeptyl", "Cetrotide", "Ovitrelle", "Utrogestan"],
};

function quickAddMedsForCountry(country: string | undefined | null): string[] {
  const normalized = country === "USA" ? "US" : country;
  if (normalized && QUICK_ADD_MEDS_BY_COUNTRY[normalized]) {
    return QUICK_ADD_MEDS_BY_COUNTRY[normalized]!;
  }
  return QUICK_ADD_MEDS_BY_COUNTRY.US!;
}

// ============================================================================
// Helpers
// ============================================================================

// getTodayString imported from @src/packages/utils/date as getToday

function mapRouteFromOCR(route: string | undefined): string {
  if (!route) return "";
  const lower = route.toLowerCase();
  if (lower.includes("oral")) return "oral";
  if (lower.includes("subcutaneous") || lower.includes("subq")) {
    return "subcutaneous";
  }
  if (lower.includes("intramuscular") || lower.includes("im")) {
    return "intramuscular";
  }
  if (lower.includes("vaginal")) return "vaginal";
  if (lower.includes("topical")) return "topical";
  if (lower.includes("patch")) return "patch";
  return "";
}

function mapTimeFromOCR(timeOfDay: string | undefined): string[] {
  if (!timeOfDay) return [];
  const lower = timeOfDay.toLowerCase();
  const times: string[] = [];
  if (lower.includes("morning")) times.push("08:00");
  if (lower.includes("afternoon") || lower.includes("noon")) {
    times.push("12:00");
  }
  if (lower.includes("evening")) times.push("18:00");
  if (lower.includes("night") || lower.includes("bedtime")) times.push("21:00");
  return times;
}

function computeEndDate(startDate: string, days: number): string {
  // `days` is the number of days the medication is taken, *including* the
  // start date. So a 7-day course beginning June 1 ends on June 7, not
  // June 8 — the calendar filter treats endDate as inclusive (med shown
  // on the endDate itself). For "Once" (days === 1), endDate === startDate.
  const offset = Math.max(0, days - 1);
  const d = new Date(startDate + "T00:00:00");
  d.setDate(d.getDate() + offset);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatTimeSummary(
  times: string[],
  t: (key: string) => string,
): string {
  if (times.length === 0) return t("add_medication.as_needed");
  const labels = times.map((time) => {
    const preset = TIME_PRESETS.find((p) => p.value === time);
    return preset ? t(preset.labelKey) : time;
  });
  return `${times.length}× ${labels.join(" & ")}`;
}

function formatDoseLabel(amount: string, unit: string): string {
  const trimmedAmount = amount.trim();
  const trimmedUnit = unit.trim();
  if (!trimmedAmount && !trimmedUnit) return "";
  return [trimmedAmount, trimmedUnit].filter(Boolean).join(" ");
}

function getRouteLabel(route: string, t: (key: string) => string): string {
  const option = ROUTE_OPTIONS.find((item) => item.value === route);
  return option ? t(option.labelKey) : route;
}

function getDeliveryMethodLabel(
  deliveryMethod: string,
  t: (key: string) => string,
): string | null {
  const option = INJECTION_DELIVERY_METHODS.find(
    (item) => item.value === deliveryMethod,
  );
  return option ? t(option.labelKey) : null;
}

function inferInventoryForm(
  route: string,
  deliveryMethod: string,
): AddMedicationInventoryForm {
  if (deliveryMethod === "pre_filled_pen") return "pen";
  if (
    deliveryMethod === "injection_solution" ||
    deliveryMethod === "lyophilized_powder" ||
    deliveryMethod === "multi_vial"
  ) {
    return "vial";
  }
  if (route === "vaginal") return "suppository";
  if (route === "oral") return "pill";
  return "supplies";
}

function inferInventoryUnitLabel(
  route: string,
  deliveryMethod: string,
): string {
  if (deliveryMethod === "pre_filled_pen") return "pens";
  if (
    deliveryMethod === "injection_solution" ||
    deliveryMethod === "lyophilized_powder" ||
    deliveryMethod === "multi_vial"
  ) {
    return "vials";
  }
  if (route === "vaginal") return "inserts";
  if (route === "oral") return "pills";
  return "units";
}

/** Pre-fill form fields from a content registry medication or supplement block */
function prefillFromContentBlock(
  block: ContentBlock<"medication"> | ContentBlock<"supplement">,
  setters: {
    setName: (v: string) => void;
    setRoute: (v: string) => void;
    setDosageUnit: (v: string) => void;
    setDeliveryMethod: (v: string) => void;
  },
) {
  setters.setName(block.title);
  if (block.kind === "supplement") {
    // Supplements are typically oral capsules/pills
    setters.setRoute("oral");
    return;
  }
  const body = block.body as MedicationBody;
  // Auto-select the first common route
  if (body.commonRoutes && body.commonRoutes.length > 0) {
    const r = deserializeMedicationRoute(body.commonRoutes[0]);
    if (ROUTES.includes(r)) {
      setters.setRoute(r);
    }
  }
  // Auto-select delivery method for injectable meds from commonForms
  if (
    body.commonRoutes?.some((r) => INJECTABLE_ROUTES.includes(r)) &&
    body.commonForms?.length > 0
  ) {
    const firstForm = body.commonForms[0]!;
    const mapped = CONTENT_FORM_TO_API_FORM[firstForm];
    if (mapped) setters.setDeliveryMethod(mapped);
  }
  // Infer dosage unit from drugClass/dosageRanges
  if (body.dosageRanges) {
    const lower = body.dosageRanges.toLowerCase();
    const matched = DOSE_UNITS.find((u) => lower.includes(u.toLowerCase()));
    if (matched) setters.setDosageUnit(matched);
  }
}

// ============================================================================
// Step Dots
// ============================================================================

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <View
      className="flex-row justify-center"
      style={{
        gap: 6,
        marginBottom: 20,
      }}
    >
      {Array.from({ length: total }).map((_, i) => (
        <View
          key={i}
          style={{
            width: i === current ? 18 : 6,
            height: 6,
            borderRadius: 3,
            backgroundColor:
              i === current
                ? EMBER.colors.warm
                : i < current
                  ? "rgba(184, 74, 22, 0.35)"
                  : "rgba(0, 0, 0, 0.06)",
          }}
        />
      ))}
    </View>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AddMedicationModal({
  visible,
  onClose,
  cycleId,
  initialStartDate,
  cycleDateBounds,
  phaseId,
  onRequestRetrievalAppointment,
  medicationRole: externalMedicationRole,
  editMedication,
  inventoryMode = false,
  onInventoryMedicationAdded,
  onMedicationCreated,
  onMedicationUpdated,
  replacingMedicationName,
}: Props) {
  const capturePress = usePressCapture("AddMedicationModal");
  const { height: windowHeight } = useWindowDimensions();
  const timezone = useTimezone();
  const { format: formatDate } = useDateFormat();
  const t = useTranslations();
  const { data: patient } = usePatient();
  const quickAddMeds = useMemo(
    () => quickAddMedsForCountry(patient?.addresses?.[0]?.country),
    [patient?.addresses],
  );
  const routeMenuItems = useMemo<AnchoredSelectMenuItem[]>(
    () => [
      ...ROUTE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
        icon: option.icon,
      })),
      {
        value: "",
        label: t("add_medication.route_option.not_specified"),
        icon: X,
        muted: true,
        separatorBefore: true,
      },
    ],
    [t],
  );
  const doseUnitMenuItems = useMemo<AnchoredSelectMenuItem[]>(
    () =>
      DOSE_UNITS.map((unit) => ({
        value: unit,
        label: DOSE_UNIT_KEYS[unit] ? t(DOSE_UNIT_KEYS[unit]) : unit,
      })),
    [t],
  );

  // Step state
  const [step, setStep] = useState<Step>(0);

  // Form state
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(
    () => initialStartDate ?? cycleDateBounds?.startDate ?? getToday(timezone),
  );
  const [endDate, setEndDate] = useState("");
  const [dosageAmount, setDosageAmount] = useState("");
  const [doseError, setDoseError] = useState<
    "invalid" | "not_positive" | "too_large" | null
  >(null);
  const [dosageUnit, setDosageUnit] = useState("");
  const [showDoseUnitDropdown, setShowDoseUnitDropdown] = useState(false);
  const doseUnitTriggerRef = useRef<View>(null);
  const [route, setRoute] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [showRouteDropdown, setShowRouteDropdown] = useState(false);
  const routeTriggerRef = useRef<View>(null);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [showCustomTimeInput, setShowCustomTimeInput] = useState(false);
  const [customTimeValue, setCustomTimeValue] = useState("");
  const [notes, setNotes] = useState("");
  const [linkToCycle, setLinkToCycle] = useState(true);
  const [inventoryQuantity, setInventoryQuantity] = useState("1");
  const [inventoryUnitLabel, setInventoryUnitLabel] = useState("");
  const [includeTakingSchedule, setIncludeTakingSchedule] = useState(false);

  // Duration state
  const [durationPreset, setDurationPreset] = useState<string | null>(null);
  const [customDurationValue, setCustomDurationValue] = useState("");
  const [customDurationUnit, setCustomDurationUnit] = useState<
    "days" | "weeks" | "months"
  >("days");

  // Database medication linkage
  const [selectedMedicationDbId, setSelectedMedicationDbId] = useState<
    string | null
  >(null);
  const [selectedDbMed, setSelectedDbMed] = useState<DbMedication | null>(null);

  // Trigger shot state
  const [isTriggerShot, setIsTriggerShot] = useState(false);

  // OCR multi-medication queue. When a prescription is scanned, all extracted
  // medications go into this queue and the user is walked through each one
  // sequentially: validate med 1 → schedule, then prefill med 2 → schedule, etc.
  const [ocrMedQueue, setOcrMedQueue] = useState<
    DocumentProcessingPrefillMedication[]
  >([]);
  const [ocrMedIndex, setOcrMedIndex] = useState(0);
  const showTriggerToggle =
    selectedDbMed?.moleculeType === "hcg" ||
    (selectedDbMed?.type != null && (selectedDbMed.type & 8) !== 0);
  const isTriggerShotContext = externalMedicationRole === "trigger_shot";
  const effectiveIsTriggerShot = isTriggerShotContext || isTriggerShot;

  // Mutations
  const createMedication = useCreateMedication();
  const updateMedication = useUpdateMedication();
  const updatePhase = useUpdatePhase();
  // The durable home of trigger-shot intent is phase.triggerShot on the
  // protocol phase (#2224) — phases are cache-shared with the cycle screens.
  const { data: cyclePhases = [] } = usePhases(cycleId || null);
  const isEditMode = !!editMedication;

  // Backend medication catalog
  const { data: dbMedications = [] } = useMedications();

  // RxNorm search (debounced)
  const { data: rxnormResults = [], isLoading: isRxNormLoading } =
    useRxNormSearch(name);

  // The source is saved before canonical processing starts. The resulting run
  // is used only to prefill this form; the complete claims/evidence remain on
  // the durable document-processing record for every other consumer.
  const transfer = useDocumentSourceTransfer();
  const resetTransfer = transfer.reset;
  const isExtracting =
    transfer.state === "uploading" ||
    transfer.state === "uploaded" ||
    transfer.state === "filing";

  // Apply a single OCR'd medication into the form fields. Used both when the
  // user first scans a prescription (med 1 of N) and when advancing through the
  // queue after each submit (med 2 of N, etc.).
  //
  // The LLM occasionally bundles dose/unit/delivery info into medicationName
  // (e.g., "PROVAMES 2 mg" or "Gonal-F 75 IU (stylo)") even when the schema
  // asks otherwise. We do a defensive regex pass to split those out so the form
  // ends up with clean fields.
  const applyOcrMedToForm = useCallback(
    (med: DocumentProcessingPrefillMedication) => {
      const TRAILING_DOSE =
        /^(.+?)\s+(\d+(?:[.,]\d+)?)\s*(mg|iu|mcg|ml|g|units?|pills?|tablets?|capsules?|drops?|patches?)\b\.?$/i;
      const PAREN_DELIVERY =
        /\s*\((stylo|pen|seringue|syringe|poudre|powder)\)\s*/i;

      let rawName = (med.medicationName ?? "").trim();
      let parsedDose = "";
      let parsedUnit = "";
      let parsedDelivery: string | null = null;

      const delivMatch = rawName.match(PAREN_DELIVERY);
      if (delivMatch) {
        const kw = delivMatch[1]!.toLowerCase();
        if (kw === "stylo" || kw === "pen") parsedDelivery = "pre_filled_pen";
        else if (kw === "seringue" || kw === "syringe") {
          parsedDelivery = "pre_filled_syringe";
        } else if (kw === "poudre" || kw === "powder") {
          parsedDelivery = "lyophilized_powder";
        }
        rawName = rawName.replace(PAREN_DELIVERY, " ").trim();
      }

      const doseMatch = rawName.match(TRAILING_DOSE);
      if (doseMatch) {
        rawName = doseMatch[1]!.trim();
        parsedDose = doseMatch[2]!.replace(",", ".");
        parsedUnit = doseMatch[3]!.toLowerCase();
      }

      if (rawName) setName(rawName);

      // OCR-provided dose/unit takes priority; fall back to the parsed values.
      const effectiveDose = med.dosage || parsedDose;
      if (effectiveDose) setDosageAmount(effectiveDose);
      const effectiveUnitRaw = med.dosageUnit || parsedUnit;
      if (effectiveUnitRaw) {
        const unit = effectiveUnitRaw.toLowerCase();
        const matched = DOSE_UNITS.find((u) => unit.includes(u));
        if (matched) setDosageUnit(matched);
      }

      // OCR-provided deliveryMethod takes priority; fall back to parsed.
      const effectiveDelivery = med.deliveryMethod || parsedDelivery;
      if (effectiveDelivery) setDeliveryMethod(effectiveDelivery);

      const mappedRoute = mapRouteFromOCR(med.route);
      if (mappedRoute) setRoute(mappedRoute);
      const mappedTimes = mapTimeFromOCR(med.timeOfDay);
      if (mappedTimes.length > 0) setSelectedTimes(mappedTimes);
    },
    [],
  );

  // Clear the per-medication fields without touching the OCR queue, so we can
  // walk to the next medication in a multi-med prescription.
  const clearMedicationFields = useCallback(() => {
    setName("");
    setDosageAmount("");
    setDosageUnit("");
    setRoute("");
    setDeliveryMethod("");
    setSelectedTimes([]);
    setEndDate("");
    setDurationPreset(null);
    setCustomDurationValue("");
    setCustomDurationUnit("days");
    setNotes("");
    setSelectedMedicationDbId(null);
    setSelectedDbMed(null);
  }, []);

  // Reset all state
  const resetState = useCallback(() => {
    setStep(0);
    setName("");
    setStartDate(
      initialStartDate ?? cycleDateBounds?.startDate ?? getToday(timezone),
    );
    setEndDate("");
    setDosageAmount("");
    setDosageUnit("");
    setShowDoseUnitDropdown(false);
    setRoute("");
    setDeliveryMethod("");
    setShowRouteDropdown(false);
    setSelectedTimes([]);
    setShowCustomTimeInput(false);
    setCustomTimeValue("");
    setNotes("");
    setLinkToCycle(true);
    setInventoryQuantity("1");
    setInventoryUnitLabel("");
    setIncludeTakingSchedule(false);
    setDurationPreset(null);
    setCustomDurationValue("");
    setCustomDurationUnit("days");
    setSelectedMedicationDbId(null);
    setSelectedDbMed(null);
    setIsTriggerShot(false);
    setOcrMedQueue([]);
    setOcrMedIndex(0);
    resetTransfer();
  }, [cycleDateBounds?.startDate, initialStartDate, resetTransfer, timezone]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const dismissSelectionMenus = useCallback(() => {
    setShowRouteDropdown(false);
    setShowDoseUnitDropdown(false);
  }, []);

  // Pre-fill form when entering edit mode
  useEffect(() => {
    if (visible && editMedication) {
      setName(editMedication.name);
      if (editMedication.dose != null) {
        setDosageAmount(String(editMedication.dose));
      }
      if (editMedication.doseUnit) setDosageUnit(editMedication.doseUnit);
      setRoute(deserializeMedicationRoute(editMedication.route));
      if (editMedication.form) setDeliveryMethod(editMedication.form);
      if (editMedication.timeTaken && editMedication.timeTaken.length > 0) {
        setSelectedTimes(editMedication.timeTaken);
      }
      if (editMedication.startDate) setStartDate(editMedication.startDate);
      if (editMedication.endDate) setEndDate(editMedication.endDate);
      if (editMedication.notes) setNotes(editMedication.notes);
      setStep(2); // Go straight to Configure
    }
  }, [visible, editMedication]);

  useEffect(() => {
    if (visible && !editMedication) {
      setStartDate(
        initialStartDate ?? cycleDateBounds?.startDate ?? getToday(timezone),
      );
    }
  }, [
    cycleDateBounds?.startDate,
    editMedication,
    initialStartDate,
    timezone,
    visible,
  ]);

  useEffect(() => {
    if (visible && !editMedication && isTriggerShotContext) {
      setIsTriggerShot(true);
    }
  }, [visible, editMedication, isTriggerShotContext]);

  // Clear delivery method when route changes to non-injectable
  const handleRouteChange = useCallback(
    (newRoute: string) => {
      setRoute(newRoute);
      const changedToNonInjectable =
        newRoute !== "" && !INJECTABLE_ROUTES.includes(newRoute);
      const nextDeliveryMethod = changedToNonInjectable ? "" : deliveryMethod;
      if (changedToNonInjectable) {
        setDeliveryMethod("");
      }
      if (inventoryMode && !inventoryUnitLabel.trim()) {
        setInventoryUnitLabel(
          inferInventoryUnitLabel(newRoute, nextDeliveryMethod),
        );
      }
    },
    [deliveryMethod, inventoryMode, inventoryUnitLabel],
  );

  useEffect(() => {
    if (!inventoryMode || inventoryUnitLabel.trim()) return;
    setInventoryUnitLabel(inferInventoryUnitLabel(route, deliveryMethod));
  }, [deliveryMethod, inventoryMode, inventoryUnitLabel, route]);

  // Time toggle
  const handleTimeToggle = useCallback((value: string) => {
    setSelectedTimes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value],
    );
  }, []);

  // Save and process the prescription source before using it to prefill the
  // medication form. The server keeps every claim, segment, party, and piece
  // of evidence even when this form only consumes the medication subset.
  const handleScanPrescription = useCallback(async () => {
    const permResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permResult.granted) {
      Alert.alert(
        t("add_medication.alert_permission_title"),
        t("add_medication.alert_permission_message"),
      );
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      quality: 0.8,
    });

    if (result.canceled || !result.assets || result.assets.length === 0) return;

    const asset = result.assets[0]!;
    const file: DocumentSourceFile = {
      uri: asset.uri,
      name: asset.fileName || "prescription.jpg",
      mimeType: resolveUploadContentType({
        name: asset.fileName || "prescription.jpg",
        uri: asset.uri,
        mimeType: asset.mimeType,
      }),
    };

    try {
      const source = await transfer.uploadSource(file, {
        destination: "medication",
        sourceContext: {
          surface: "add_medication_modal",
          route: "AddMedicationModal",
          form: "medication",
          allowBackgroundProcessing: true,
        },
      });
      const outcome = await transfer.processSource(source);
      const projected = projectDocumentProcessingRunToPrefill(
        outcome.run,
        file.name,
      );
      const meds = projected.medications ?? [];
      setOcrMedQueue(meds);
      setOcrMedIndex(0);
      if (meds.length > 0) {
        applyOcrMedToForm(meds[0]!);
      } else {
        Alert.alert(
          t("add_medication.alert_ocr_no_meds_title"),
          t("add_medication.alert_ocr_no_meds_message"),
        );
      }
      const extractedDate = projected.documentDate || meds[0]?.startDate;
      if (extractedDate) setStartDate(extractedDate);
      setStep(2);
    } catch (error) {
      console.error("[AddMedicationModal] canonical processing error:", error);
      Alert.alert(
        t("add_medication.alert_ocr_failed_title"),
        error instanceof Error
          ? error.message
          : t("add_medication.alert_ocr_failed_message"),
      );
      setStep(2);
    }
  }, [applyOcrMedToForm, t, transfer]);

  // ── 3-tier medication search ──────────────────────────────────────
  // Tier 1: Backend catalog (real UUIDs)
  // Tier 2: RxNorm API (comprehensive FDA drugs)
  // Tier 3: Content registry (supplements + educational)

  const allMedBlocks = useMemo(() => getBlocksByKind("medication"), []);
  const allSupplementBlocks = useMemo(() => getBlocksByKind("supplement"), []);

  const searchResults = useMemo((): MedSearchResult[] => {
    if (!name.trim() || name.length < 2) return [];
    const lower = name.toLowerCase();
    const seen = new Set<string>();
    const results: MedSearchResult[] = [];

    // Normalize for dedup: strip non-alphanumeric so "Gonal-F" and "Gonal F" match
    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

    // Relevance score: name-starts-with (0) > name-contains (1) > molecule/subtitle (2)
    function relevance(itemName: string, molecule?: string | null): number {
      if (itemName.toLowerCase().startsWith(lower)) return 0;
      if (itemName.toLowerCase().includes(lower)) return 1;
      if (molecule?.toLowerCase().includes(lower)) return 2;
      return 3;
    }

    // Tier 1: RxNorm API — authoritative source for US drugs, with route/form variants
    // Humanize TTY codes for subtitle fallback
    const ttyLabels: Record<string, string> = {
      BN: "Brand name",
      IN: "Ingredient",
      SBD: "Branded drug",
      SCD: "Clinical drug",
      GPCK: "Generic pack",
      BPCK: "Brand pack",
    };

    const rxMatches: (MedSearchResult & { _score: number })[] = [];
    for (const rx of rxnormResults) {
      const parsed = parseDrugName(rx.name);
      const displayName = parsed?.brandName ?? parsed?.name ?? rx.name;
      const baseName = normalize(displayName);
      // Deduplicate by name + route/form combo so "Progesterone Oral" and "Progesterone Vaginal" are separate
      const routeKey = parsed?.route || parsed?.form || "";
      const key = routeKey ? `${baseName}|${normalize(routeKey)}` : baseName;
      if (seen.has(key)) continue;
      seen.add(key);
      // Also mark the base name as seen so DB doesn't duplicate
      seen.add(baseName);

      // Build subtitle: prefer parsed form/dose, then enrich from DB if available, then humanized TTY
      let subtitle = "";
      if (parsed && (parsed.form || parsed.dose)) {
        subtitle = [
          parsed.form,
          parsed.dose && `${parsed.dose} ${parsed.doseUnit}`,
        ]
          .filter(Boolean)
          .join(" · ");
      } else {
        // Try to enrich from our DB (molecule name, default route)
        const dbMatch = findMedicationByName(dbMedications, displayName);
        if (dbMatch) {
          subtitle = [
            dbMatch.molecule,
            dbMatch.defaultRoute ?? dbMatch.intakeType,
          ]
            .filter(Boolean)
            .join(" · ");
        } else {
          subtitle = ttyLabels[rx.tty] ?? rx.tty;
        }
      }

      rxMatches.push({
        id: `rx-${rx.rxcui}`,
        name: displayName,
        subtitle,
        source: "rxnorm",
        rxnormResult: rx,
        _score: relevance(displayName),
      });
    }
    rxMatches.sort((a, b) => a._score - b._score);
    for (const m of rxMatches) results.push(m);

    // Tier 2: Backend catalog — only for meds not already covered by RxNorm
    const dbMatches: (MedSearchResult & { _score: number })[] = [];
    for (const med of dbMedications) {
      if (!med.name) continue;
      if (
        !med.name.toLowerCase().includes(lower) &&
        !med.molecule?.toLowerCase().includes(lower)
      ) {
        continue;
      }
      const key = normalize(med.name);
      if (seen.has(key)) continue;
      seen.add(key);
      dbMatches.push({
        id: `db-${med.id}`,
        name: med.name,
        subtitle: [med.molecule, med.defaultRoute ?? med.intakeType]
          .filter(Boolean)
          .join(" · "),
        source: "db",
        dbMed: med,
        _score: relevance(med.name, med.molecule),
      });
    }
    dbMatches.sort((a, b) => a._score - b._score);
    for (const m of dbMatches) results.push(m);

    // Tier 3: Content registry (meds + supplements)
    for (const block of allMedBlocks) {
      const body = block.body as MedicationBody;
      const matches =
        block.title.toLowerCase().includes(lower) ||
        body.genericName?.toLowerCase().includes(lower) ||
        body.brandsByCountry?.some((e) =>
          e.brands.some((b) => b.toLowerCase().includes(lower)),
        );
      if (!matches) continue;
      const key = normalize(block.title);
      if (seen.has(key)) continue;
      seen.add(key);
      results.push({
        id: `content-${block.id}`,
        name: block.title,
        subtitle: [body.genericName, body.commonRoutes?.[0]]
          .filter(Boolean)
          .join(" · "),
        source: "content",
        contentBlock: block,
      });
    }

    for (const block of allSupplementBlocks) {
      const body = block.body as SupplementBody;
      const matches =
        block.title.toLowerCase().includes(lower) ||
        body.name?.toLowerCase().includes(lower);
      if (!matches) continue;
      const key = normalize(block.title);
      if (seen.has(key)) continue;
      seen.add(key);
      results.push({
        id: `content-${block.id}`,
        name: block.title,
        subtitle: body.name,
        source: "content",
        contentBlock: block,
      });
    }

    if (isTriggerShotContext) {
      const normalizedQuery = normalize(lower);
      for (const medName of TRIGGER_MEDICATION_SEARCH_FALLBACKS) {
        const key = normalize(medName);
        if (seen.has(key)) continue;
        if (
          !medName.toLowerCase().includes(lower) &&
          !key.includes(normalizedQuery)
        ) {
          continue;
        }
        seen.add(key);
        results.push({
          id: `trigger-preset-${key}`,
          name: medName,
          subtitle: "",
          source: "preset",
        });
      }
    }

    return results.slice(0, 10);
  }, [
    name,
    dbMedications,
    rxnormResults,
    allMedBlocks,
    allSupplementBlocks,
    isTriggerShotContext,
  ]);

  /** Handle selecting a unified search result */
  const handleSelectResult = useCallback(
    (result: MedSearchResult) => {
      setName(result.name);

      // Auto-set unit + route from dosage presets (fallback for when DB fields are null)
      const presets = getDosagePresetForMedicationName(result.name);
      if (presets) {
        setDosageUnit(presets.unit);
        if (ROUTES.includes(presets.route)) setRoute(presets.route);
      }

      if (result.source === "db" && result.dbMed) {
        // Backend catalog — link UUID + auto-fill
        setSelectedMedicationDbId(result.dbMed.id);
        setSelectedDbMed(result.dbMed);
        const catalogRoute = deserializeMedicationRoute(
          result.dbMed.defaultRoute ?? result.dbMed.intakeType,
        );
        if (ROUTES.includes(catalogRoute)) setRoute(catalogRoute);
        if (result.dbMed.doseUnit) setDosageUnit(result.dbMed.doseUnit);
        if (result.dbMed.dose) setDosageAmount(String(result.dbMed.dose));
        // Auto-detect trigger shot candidate
        if (
          result.dbMed.moleculeType === "hcg" ||
          (result.dbMed.type != null && (result.dbMed.type & 8) !== 0)
        ) {
          setIsTriggerShot(true);
        }
      } else if (result.source === "rxnorm" && result.rxnormResult) {
        // RxNorm — parse name for auto-fill
        const parsed = parseDrugName(result.rxnormResult.name);
        if (parsed) {
          if (parsed.route && ROUTES.includes(parsed.route)) {
            setRoute(parsed.route);
          }
          if (parsed.dose) setDosageAmount(parsed.dose);
          if (parsed.doseUnit) {
            const matched = DOSE_UNITS.find(
              (u) => u.toLowerCase() === parsed.doseUnit.toLowerCase(),
            );
            if (matched) setDosageUnit(matched);
          }
        }
        // Try to link to backend catalog by name
        const dbMatch = findMedicationByName(dbMedications, result.name);
        if (dbMatch) {
          setSelectedMedicationDbId(dbMatch.id);
          setSelectedDbMed(dbMatch);
        }
      } else if (result.contentBlock) {
        // Content registry — use prefill helper
        prefillFromContentBlock(result.contentBlock, {
          setName,
          setRoute,
          setDosageUnit,
          setDeliveryMethod,
        });
        // Try DB linkage
        const dbMatch = findMedicationByName(dbMedications, result.name);
        if (dbMatch) {
          setSelectedMedicationDbId(dbMatch.id);
          setSelectedDbMed(dbMatch);
        }
      }

      setStep(2);
    },
    [dbMedications],
  );

  // Quick add
  const handleQuickAdd = useCallback(
    (medName: string) => {
      const dbMatch = findMedicationByName(dbMedications, medName);
      const presets = getDosagePresetForMedicationName(medName);
      if (dbMatch) {
        setName(dbMatch.name ?? medName);
        setSelectedMedicationDbId(dbMatch.id);
        setSelectedDbMed(dbMatch);
        const catalogRoute = deserializeMedicationRoute(
          dbMatch.defaultRoute ?? dbMatch.intakeType,
        );
        if (ROUTES.includes(catalogRoute)) {
          setRoute(catalogRoute);
        } else if (presets && ROUTES.includes(presets.route)) {
          setRoute(presets.route);
        }
        if (dbMatch.doseUnit) setDosageUnit(dbMatch.doseUnit);
        else if (presets) setDosageUnit(presets.unit);
        if (dbMatch.dose) setDosageAmount(String(dbMatch.dose));
      } else {
        if (presets) {
          setDosageUnit(presets.unit);
          if (ROUTES.includes(presets.route)) setRoute(presets.route);
        }
        const block = getMedicationByBrandName(medName);
        if (block) {
          prefillFromContentBlock(block, {
            setName,
            setRoute,
            setDosageUnit,
            setDeliveryMethod,
          });
        } else {
          setName(medName);
        }
      }
      setStep(2);
    },
    [dbMedications],
  );

  // Compute end date from duration
  const computedEndDate = useMemo(() => {
    if (!durationPreset) return endDate || null;
    const preset = DURATION_PRESETS.find((p) => p.labelKey === durationPreset);
    if (preset && preset.days != null) {
      return computeEndDate(startDate, preset.days);
    }
    if (durationPreset === "Custom" && customDurationValue) {
      const val = parseInt(customDurationValue, 10);
      if (!val || val <= 0) return null;
      const multiplier =
        customDurationUnit === "weeks"
          ? 7
          : customDurationUnit === "months"
            ? 30
            : 1;
      return computeEndDate(startDate, val * multiplier);
    }
    return null; // Ongoing
  }, [
    durationPreset,
    startDate,
    endDate,
    customDurationValue,
    customDurationUnit,
  ]);

  // Submit
  // After submitting a dose period, reset dose/duration fields for a new period
  // but keep the medication identity (name, route, DB linkage, times, cycle link)
  const handleAddAnotherDosePeriod = useCallback(() => {
    // The next period starts the day after the current one ends
    const nextStartDate = computedEndDate
      ? computeEndDate(computedEndDate, 1)
      : getToday(timezone);

    setDosageAmount("");
    setStartDate(nextStartDate);
    setEndDate("");
    setDurationPreset(null);
    setCustomDurationValue("");
    setCustomDurationUnit("days");
    setNotes("");
    // Go back to configure step (step 2) with same medication pre-filled
    setStep(2);
  }, [computedEndDate, timezone]);

  const buildInventoryEntry =
    useCallback((): AddMedicationInventoryEntry | null => {
      if (!inventoryMode) return null;
      const quantity = Number.parseInt(inventoryQuantity, 10);
      const safeQuantity =
        Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      const unitLabel =
        inventoryUnitLabel.trim() ||
        inferInventoryUnitLabel(route, deliveryMethod);

      return {
        name: name.trim(),
        dosage: formatDoseLabel(dosageAmount, dosageUnit),
        doseAmount: (() => {
          const parsed = parseDoseInput(dosageAmount);
          return parsed.ok ? parsed.value : null;
        })(),
        doseUnit: dosageUnit || null,
        route: route || null,
        routeLabel: route ? getRouteLabel(route, t) : "",
        deliveryMethod: deliveryMethod || null,
        deliveryMethodLabel: deliveryMethod
          ? getDeliveryMethodLabel(deliveryMethod, t)
          : null,
        form: inferInventoryForm(route, deliveryMethod),
        quantity: safeQuantity,
        unitLabel,
        medicationId: selectedMedicationDbId,
      };
    }, [
      deliveryMethod,
      dosageAmount,
      dosageUnit,
      inventoryMode,
      inventoryQuantity,
      inventoryUnitLabel,
      name,
      route,
      selectedMedicationDbId,
      t,
    ]);

  const handleSubmit = useCallback(async () => {
    // Field-level dose validation shared with add-another and OCR (#2228):
    // bare parseFloat accepted "12abc" as 12 and "1,5" as 1, and let zero or
    // negative doses through to the API.
    const parsedDose = parseDoseInput(dosageAmount);
    if (!parsedDose.ok) {
      setDoseError(parsedDose.reason);
      return;
    }

    const shouldCreateMedication = !inventoryMode || includeTakingSchedule;
    const inventoryEntry = inventoryMode ? buildInventoryEntry() : null;

    if (cycleDateBounds) {
      const startsBeforeCycle = startDate < cycleDateBounds.startDate;
      const startsAfterCycle =
        !!cycleDateBounds.endDate && startDate > cycleDateBounds.endDate;
      const endsAfterCycle =
        !!cycleDateBounds.endDate &&
        !!computedEndDate &&
        computedEndDate > cycleDateBounds.endDate;
      if (startsBeforeCycle || startsAfterCycle || endsAfterCycle) {
        Alert.alert(
          t("add_medication.historical_date_error_title"),
          t("add_medication.historical_date_error_message"),
        );
        return;
      }
    }

    if (editMedication) {
      // Edit mode — update existing medication
      const nextDose = parsedDose.value;
      const nextDoseUnit = dosageUnit || null;
      try {
        // A name edited here is a patient display override (#319): it
        // persists as displayName without ever touching catalog names.
        // Restoring the original name clears the override.
        const trimmedName = name.trim();
        const displayNameUpdate =
          trimmedName && trimmedName !== editMedication.name
            ? { displayName: trimmedName }
            : {};
        await updateMedication.mutateAsync({
          medicationId: editMedication.id,
          ...displayNameUpdate,
          ...(startDate ? { startDate } : {}),
          endDate: computedEndDate || null,
          dose: nextDose,
          doseUnit: nextDoseUnit,
          // The edit form always shows the record's current schedule, so an
          // empty selection is the user explicitly clearing every time — send
          // [] (which also disables the linked reminder) rather than omitting
          // the field, which the update hook reads as "leave times unchanged"
          // (#2227/#2233). Create mode keeps omitting when empty.
          timeTaken: selectedTimes,
          notes: notes || null,
          route: serializeMedicationRoute(route),
          form: deliveryMethod
            ? mapDeliveryMethodToApiForm(deliveryMethod)
            : null,
        });
        onMedicationUpdated?.({
          id: editMedication.id,
          name: trimmedName || editMedication.name,
          dose: nextDose,
          doseUnit: nextDoseUnit,
          previous: {
            dose: editMedication.dose ?? null,
            doseUnit: editMedication.doseUnit ?? null,
          },
        });
        handleClose();
      } catch (err) {
        Alert.alert(
          t("add_medication.alert_update_failed_title"),
          err instanceof Error
            ? err.message
            : t("add_medication.alert_update_failed_message"),
        );
      }
      return;
    }

    if (shouldCreateMedication) {
      // Create mode
      const role = effectiveIsTriggerShot
        ? "trigger_shot"
        : (externalMedicationRole ?? "standard");
      const newDose = parsedDose.value;
      const newDoseUnit = dosageUnit || null;

      try {
        await createMedication.mutateAsync({
          name,
          startDate,
          endDate: computedEndDate,
          dose: newDose,
          doseUnit: newDoseUnit,
          timeTaken: selectedTimes.length > 0 ? selectedTimes : undefined,
          notes: notes || null,
          route: serializeMedicationRoute(route),
          form: deliveryMethod
            ? mapDeliveryMethodToApiForm(deliveryMethod)
            : null,
          cycleId: linkToCycle && cycleId ? cycleId : null,
          phaseId: linkToCycle && phaseId ? phaseId : null,
          medicationId: selectedMedicationDbId,
          medicationRole: role,
        });
        onMedicationCreated?.({
          name,
          dose: newDose,
          doseUnit: newDoseUnit,
        });
      } catch (err) {
        console.error("[AddMedicationModal] createMedication failed:", err);
        Alert.alert(
          t("add_medication.alert_update_failed_title"),
          err instanceof Error
            ? err.message
            : t("add_medication.alert_update_failed_message"),
        );
        return;
      }

      // Give the trigger shot its durable home: the medication record has no
      // role field in the API, so reload and cross-surface behavior (stim
      // chart, cycle header, hub trigger step) derive from phase.triggerShot
      // on the cycle's protocol phase (#2224).
      if (effectiveIsTriggerShot && linkToCycle && cycleId) {
        const targetPhase = resolveTriggerShotPhase(cyclePhases, phaseId);
        if (targetPhase) {
          try {
            await updatePhase.mutateAsync({
              cycleId,
              phaseId: targetPhase.id,
              body: {
                triggerShot: buildTriggerShotPatch({
                  existing: targetPhase.triggerShot,
                  startDate,
                  time: selectedTimes[0] ?? null,
                  medicationName: name,
                }),
              },
            });
          } catch (err) {
            // The medication itself saved — surface the partial failure
            // instead of silently dropping the durable trigger date.
            console.error(
              "[AddMedicationModal] trigger phase update failed:",
              err,
            );
            Alert.alert(
              t("add_medication.alert_trigger_phase_failed_title"),
              t("add_medication.alert_trigger_phase_failed_message"),
            );
          }
        }
      }
    }

    if (inventoryEntry) {
      try {
        await onInventoryMedicationAdded?.(inventoryEntry);
      } catch (err) {
        Alert.alert(
          t("add_medication.alert_update_failed_title"),
          err instanceof Error
            ? err.message
            : t("add_medication.alert_update_failed_message"),
        );
        return;
      }
    }

    // Trigger shot → auto-open retrieval appointment
    if (
      shouldCreateMedication &&
      effectiveIsTriggerShot &&
      onRequestRetrievalAppointment &&
      selectedTimes.length > 0
    ) {
      const triggerTime = selectedTimes[0]!;
      const [hours, minutes] = triggerTime.split(":").map(Number);
      const triggerDate = new Date(startDate + "T00:00:00");
      triggerDate.setHours(hours!, minutes);
      triggerDate.setHours(triggerDate.getHours() + 36);
      const retrievalDate = `${triggerDate.getFullYear()}-${String(triggerDate.getMonth() + 1).padStart(2, "0")}-${String(triggerDate.getDate()).padStart(2, "0")}`;
      const retrievalTime = `${String(triggerDate.getHours()).padStart(2, "0")}:${String(triggerDate.getMinutes()).padStart(2, "0")}`;
      onRequestRetrievalAppointment({
        type: "Procedure",
        subType: "Egg Retrieval",
        suggestedDate: retrievalDate,
        suggestedTime: retrievalTime,
      });
    }

    // Multi-medication OCR queue: if more meds remain in the scanned
    // prescription, walk to the next one instead of closing.
    if (shouldCreateMedication && ocrMedIndex < ocrMedQueue.length - 1) {
      const nextIndex = ocrMedIndex + 1;
      setOcrMedIndex(nextIndex);
      clearMedicationFields();
      applyOcrMedToForm(ocrMedQueue[nextIndex]!);
      setStep(2);
      return;
    }

    handleClose();
  }, [
    editMedication,
    updateMedication,
    createMedication,
    inventoryMode,
    includeTakingSchedule,
    cycleDateBounds,
    computedEndDate,
    startDate,
    buildInventoryEntry,
    ocrMedQueue,
    ocrMedIndex,
    clearMedicationFields,
    applyOcrMedToForm,
    name,
    dosageAmount,
    dosageUnit,
    selectedTimes,
    notes,
    route,
    linkToCycle,
    cycleId,
    phaseId,
    handleClose,
    selectedMedicationDbId,
    effectiveIsTriggerShot,
    externalMedicationRole,
    updatePhase,
    cyclePhases,
    onRequestRetrievalAppointment,
    onInventoryMedicationAdded,
    onMedicationCreated,
    onMedicationUpdated,
    deliveryMethod,
    t,
  ]);

  // Submit current entry and start a new dose period for the same medication
  const handleSubmitAndAddAnother = useCallback(async () => {
    // Same field-level validation as the normal submit path (#2228).
    const parsedDose = parseDoseInput(dosageAmount);
    if (!parsedDose.ok) {
      setDoseError(parsedDose.reason);
      return;
    }

    if (cycleDateBounds) {
      const startsBeforeCycle = startDate < cycleDateBounds.startDate;
      const startsAfterCycle =
        !!cycleDateBounds.endDate && startDate > cycleDateBounds.endDate;
      const endsAfterCycle =
        !!cycleDateBounds.endDate &&
        !!computedEndDate &&
        computedEndDate > cycleDateBounds.endDate;
      if (startsBeforeCycle || startsAfterCycle || endsAfterCycle) {
        Alert.alert(
          t("add_medication.historical_date_error_title"),
          t("add_medication.historical_date_error_message"),
        );
        return;
      }
    }

    const role = effectiveIsTriggerShot
      ? "trigger_shot"
      : (externalMedicationRole ?? "standard");

    try {
      await createMedication.mutateAsync({
        name,
        startDate,
        endDate: computedEndDate,
        dose: parsedDose.value,
        doseUnit: dosageUnit || null,
        timeTaken: selectedTimes.length > 0 ? selectedTimes : undefined,
        notes: notes || null,
        route: serializeMedicationRoute(route),
        form: deliveryMethod
          ? mapDeliveryMethodToApiForm(deliveryMethod)
          : null,
        cycleId: linkToCycle && cycleId ? cycleId : null,
        phaseId: linkToCycle && phaseId ? phaseId : null,
        medicationId: selectedMedicationDbId,
        medicationRole: role,
      });
    } catch (err) {
      // Keep the current dose period on screen for retry — advancing (or an
      // unhandled rejection) would silently drop the entry (#2228).
      console.error(
        "[AddMedicationModal] createMedication (add another) failed:",
        err,
      );
      Alert.alert(
        t("add_medication.alert_update_failed_title"),
        err instanceof Error
          ? err.message
          : t("add_medication.alert_update_failed_message"),
      );
      return;
    }

    handleAddAnotherDosePeriod();
  }, [
    cycleDateBounds,
    startDate,
    computedEndDate,
    createMedication,
    name,
    dosageAmount,
    dosageUnit,
    selectedTimes,
    notes,
    route,
    linkToCycle,
    cycleId,
    phaseId,
    selectedMedicationDbId,
    effectiveIsTriggerShot,
    externalMedicationRole,
    handleAddAnotherDosePeriod,
    deliveryMethod,
    t,
  ]);

  // Shared styles
  const inputStyle = {
    fontSize: 14,
    color: EMBER.colors.text,
    borderWidth: 1,
    borderColor: EMBER.colors.border,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    ...(Platform.OS === "web" ? { outlineStyle: "none" as const } : {}),
  };

  const labelStyle = {
    fontSize: 13,
    fontWeight: "600" as const,
    color: EMBER.colors.textMid,
    marginBottom: 6,
  };

  // ============================================================================
  // Step 0 — Entry Method
  // ============================================================================

  function renderStep0() {
    return (
      <View style={{ padding: 24 }}>
        <StepDots current={0} total={4} />

        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textMid,
            textAlign: "center",
            marginBottom: 24,
          }}
        >
          {isTriggerShotContext
            ? t("add_medication.trigger_entry_intro")
            : t("add_medication.entry_intro")}
        </Text>

        {/* Search by name */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("add_medication.entry_search_by_name")),
              "add_medication.entry_search_by_name",
            );
            setStep(1);
          }}
          className="flex-row items-center"
          style={{
            padding: 16,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: EMBER.colors.border,
            marginBottom: 10,
          }}
        >
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              justifyContent: "center",
              alignItems: "center",
              marginRight: 14,
            }}
          >
            <Search size={20} color={EMBER.colors.warm} />
          </View>
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                fontWeight: "600",
                color: EMBER.colors.text,
              }}
            >
              {t("add_medication.entry_search_by_name")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textLight,
                marginTop: 2,
              }}
            >
              {t("add_medication.entry_search_by_name_help")}
            </Text>
          </View>
          <ChevronRight size={18} color={EMBER.colors.textFaint} />
        </Pressable>

        {/* Scan prescription */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                t(
                  isExtracting
                    ? "add_medication.entry_reading_prescription"
                    : "add_medication.entry_scan_prescription",
                ),
              ),
              "add_medication.entry_scan_prescription",
            );
            handleScanPrescription();
          }}
          disabled={isExtracting}
          className="flex-row items-center"
          style={{
            padding: 16,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: EMBER.colors.border,
            marginBottom: 10,
            opacity: isExtracting ? 0.6 : 1,
          }}
        >
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              justifyContent: "center",
              alignItems: "center",
              marginRight: 14,
            }}
          >
            {isExtracting ? (
              <ActivityIndicator size="small" color={EMBER.colors.warm} />
            ) : (
              <Camera size={20} color={EMBER.colors.warm} />
            )}
          </View>
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                fontWeight: "600",
                color: EMBER.colors.text,
              }}
            >
              {isExtracting
                ? t("add_medication.entry_reading_prescription")
                : t("add_medication.entry_scan_prescription")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textLight,
                marginTop: 2,
              }}
            >
              {t("add_medication.entry_scan_prescription_help")}
            </Text>
          </View>
          {!isExtracting && (
            <ChevronRight size={18} color={EMBER.colors.textFaint} />
          )}
        </Pressable>

        {/* Quick add — only if cycleId exists */}
        {cycleId ? (
          <View style={{ marginTop: 14 }}>
            <View
              className="flex-row items-center"
              style={{
                marginBottom: 12,
                gap: 6,
              }}
            >
              <Zap size={14} color={EMBER.colors.gold} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  color: EMBER.colors.textMid,
                }}
              >
                {t("add_medication.quick_add")}
              </Text>
            </View>
            <View className="flex-row flex-wrap" style={{ gap: 8 }}>
              {quickAddMeds.map((med) => (
                <Pressable
                  key={med}
                  onPress={() => {
                    capturePress(pressLabel(med), "quick_add_medication");
                    handleQuickAdd(med);
                  }}
                  style={{
                    paddingVertical: 8,
                    paddingHorizontal: 14,
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor: EMBER.colors.border,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{ fontSize: 13, color: EMBER.colors.text }}
                  >
                    {med}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>
        ) : null}
      </View>
    );
  }

  // ============================================================================
  // Step 1 — Search & Select
  // ============================================================================

  function renderStep1() {
    return (
      <View style={{ padding: 24, maxHeight: windowHeight * 0.7 }}>
        <StepDots current={1} total={4} />

        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textMid,
            marginBottom: 16,
          }}
        >
          {isTriggerShotContext
            ? t("add_medication.trigger_search_intro")
            : t("add_medication.search_intro")}
        </Text>

        <MoonSearchInput
          value={name}
          onChangeText={setName}
          placeholder={
            isTriggerShotContext
              ? t("add_medication.trigger_search_placeholder")
              : t("add_medication.search_placeholder")
          }
          autoFocus
          onClear={() => setName("")}
        />

        {/* Search results */}
        {searchResults.length > 0 && (
          <ScrollView
            style={{ marginTop: 12, maxHeight: 280 }}
            showsVerticalScrollIndicator={false}
          >
            {searchResults.map((result) => {
              const sourceBadge = SOURCE_BADGE_CONFIG[result.source];
              return (
                <Pressable
                  key={result.id}
                  onPress={() => {
                    capturePress(
                      pressLabel(result.name),
                      "select_medication_result",
                    );
                    handleSelectResult(result);
                  }}
                  className="flex-row items-center"
                  style={({ pressed }) => ({
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    borderRadius: 10,
                    backgroundColor: pressed
                      ? "rgba(139, 58, 26, 0.06)"
                      : "transparent",
                    marginBottom: 2,
                  })}
                >
                  <View style={{ flex: 1 }}>
                    <View
                      className="flex-row items-center"
                      style={{
                        gap: 6,
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          fontWeight: "600",
                          color: EMBER.colors.text,
                        }}
                      >
                        {result.name}
                      </Text>
                      <Tag label={sourceBadge.label} tone={sourceBadge.tone} />
                    </View>
                    {result.subtitle ? (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          color: EMBER.colors.textLight,
                          marginTop: 1,
                        }}
                      >
                        {result.subtitle}
                      </Text>
                    ) : null}
                  </View>
                  <ChevronRight size={16} color={EMBER.colors.textFaint} />
                </Pressable>
              );
            })}
          </ScrollView>
        )}

        {/* Loading indicator for RxNorm */}
        {name.trim().length >= 2 &&
          searchResults.length === 0 &&
          isRxNormLoading && (
            <View style={{ marginTop: 16, alignItems: "center" }}>
              <ActivityIndicator size="small" color={EMBER.colors.warm} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: EMBER.colors.textLight,
                  marginTop: 6,
                }}
              >
                {t("add_medication.search_loading")}
              </Text>
            </View>
          )}

        {/* No results hint — allow manual entry */}
        {name.trim().length >= 2 &&
          searchResults.length === 0 &&
          !isRxNormLoading && (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textLight,
                marginTop: 12,
                textAlign: "center",
              }}
            >
              {isTriggerShotContext
                ? t("add_medication.trigger_search_no_match")
                : t("add_medication.search_no_match")}
            </Text>
          )}

        {/* Next button for manual entry */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("add_medication.next")),
              "add_medication.next",
            );
            if (name.trim()) {
              // Try DB linkage first, then content registry
              const dbMatch = findMedicationByName(dbMedications, name.trim());
              const presets = getDosagePresetForMedicationName(name.trim());
              if (dbMatch) {
                setSelectedMedicationDbId(dbMatch.id);
                setSelectedDbMed(dbMatch);
                const catalogRoute = deserializeMedicationRoute(
                  dbMatch.defaultRoute ?? dbMatch.intakeType,
                );
                if (ROUTES.includes(catalogRoute)) {
                  setRoute(catalogRoute);
                } else if (presets && ROUTES.includes(presets.route)) {
                  setRoute(presets.route);
                }
                if (dbMatch.doseUnit) setDosageUnit(dbMatch.doseUnit);
                else if (presets) setDosageUnit(presets.unit);
              } else {
                if (presets) {
                  setDosageUnit(presets.unit);
                  if (ROUTES.includes(presets.route)) setRoute(presets.route);
                }
                const block = getMedicationByBrandName(name.trim());
                if (block) {
                  prefillFromContentBlock(block, {
                    setName,
                    setRoute,
                    setDosageUnit,
                    setDeliveryMethod,
                  });
                }
              }
              setStep(2);
            }
          }}
          disabled={!name.trim()}
          style={{
            marginTop: 16,
            backgroundColor: name.trim()
              ? EMBER.colors.warm
              : "rgba(0, 0, 0, 0.06)",
            borderRadius: 12,
            paddingVertical: 14,
            alignItems: "center",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "600",
              color: name.trim() ? "#FFFFFF" : EMBER.colors.textFaint,
            }}
          >
            {t("add_medication.next")}
          </Text>
        </Pressable>
      </View>
    );
  }

  // ============================================================================
  // Step 2 — Configure
  // ============================================================================

  function renderStep2() {
    const showScheduleFields = !inventoryMode || includeTakingSchedule;
    const inferredInventoryUnit = inferInventoryUnitLabel(
      route,
      deliveryMethod,
    );

    return (
      <ScrollView style={{ padding: 24 }} showsVerticalScrollIndicator={false}>
        <StepDots current={2} total={4} />

        {replacingMedicationName ? (
          <View
            style={{
              backgroundColor: "rgba(196, 149, 106, 0.12)",
              borderRadius: 10,
              padding: 12,
              marginBottom: 16,
              borderWidth: 1,
              borderColor: "rgba(196, 149, 106, 0.28)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.warm,
              }}
            >
              {t("care.protocol_notes.swap_replacing_banner", {
                params: { medication: replacingMedicationName },
              })}
            </Text>
          </View>
        ) : null}

        {ocrMedQueue.length > 1 ? (
          <View
            style={{
              backgroundColor: "rgba(232, 109, 67, 0.08)",
              borderRadius: 10,
              padding: 12,
              marginBottom: 16,
              borderWidth: 1,
              borderColor: "rgba(232, 109, 67, 0.2)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.warm,
              }}
            >
              {t("add_medication.ocr_queue_progress", {
                params: {
                  current: String(ocrMedIndex + 1),
                  total: String(ocrMedQueue.length),
                },
              })}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textMid,
                marginTop: 2,
              }}
            >
              {t("add_medication.ocr_queue_help")}
            </Text>
          </View>
        ) : null}

        {isTriggerShotContext ? (
          <View
            style={{
              backgroundColor: "rgba(234, 179, 8, 0.08)",
              borderRadius: 10,
              padding: 12,
              marginBottom: 16,
              borderWidth: 1,
              borderColor: "rgba(234, 179, 8, 0.22)",
            }}
          >
            <View
              className="flex-row items-center"
              style={{ gap: 8, marginBottom: 4 }}
            >
              <Zap size={14} color={EMBER.colors.gold} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                }}
              >
                {t("add_medication.trigger_context_title")}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textMid,
                lineHeight: 17,
              }}
            >
              {t("add_medication.trigger_context_body")}
            </Text>
          </View>
        ) : null}

        {/* Name + inline route (when route is known) */}
        <MoonFormField
          label={t("add_medication.medication_name")}
          style={{ marginBottom: 16 }}
        >
          <View
            className="flex-row"
            style={{
              gap: 0,
              position: "relative",
              zIndex: 20,
            }}
          >
            <TextInput
              value={name}
              onChangeText={setName}
              placeholder={t("add_medication.medication_name")}
              placeholderTextColor={EMBER.colors.textFaint}
              style={{
                ...inputStyle,
                flex: 1,
                borderTopRightRadius: route ? 0 : 12,
                borderBottomRightRadius: route ? 0 : 12,
              }}
            />
            {route ? (
              <>
                <Pressable
                  ref={routeTriggerRef}
                  onPress={() => {
                    capturePress(
                      pressLabel(t("add_medication.summary_route")),
                      "open_route_dropdown",
                    );
                    setShowDoseUnitDropdown(false);
                    setShowRouteDropdown((current) => !current);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={t("add_medication.summary_route")}
                  accessibilityState={{ expanded: showRouteDropdown }}
                  className="flex-row items-center"
                  style={{
                    gap: 4,
                    paddingHorizontal: 12,
                    borderWidth: 1,
                    borderLeftWidth: 0,
                    borderColor: EMBER.colors.border,
                    borderTopRightRadius: 12,
                    borderBottomRightRadius: 12,
                    backgroundColor: "rgba(0,0,0,0.03)",
                  }}
                >
                  {(() => {
                    const opt = ROUTE_OPTIONS.find((o) => o.value === route);
                    const IconComp = opt?.icon ?? Pill;
                    return <IconComp size={13} color={EMBER.colors.warm} />;
                  })()}
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      fontWeight: "500",
                      color: EMBER.colors.warm,
                    }}
                  >
                    {(() => {
                      const opt = ROUTE_OPTIONS.find((o) => o.value === route);
                      return opt ? t(opt.labelKey) : route;
                    })()}
                  </Text>
                  <ChevronDown size={12} color={EMBER.colors.textLight} />
                </Pressable>
                <AnchoredSelectMenu
                  visible={showRouteDropdown}
                  triggerRef={routeTriggerRef}
                  value={route}
                  items={routeMenuItems}
                  align="right"
                  width={200}
                  maxHeight={340}
                  accentColor={EMBER.colors.warm}
                  captureEscape={false}
                  testID="medication-route-menu"
                  onClose={dismissSelectionMenus}
                  onSelect={(nextRoute) => {
                    handleRouteChange(nextRoute);
                    setShowRouteDropdown(false);
                  }}
                />
              </>
            ) : null}
          </View>
        </MoonFormField>

        {/* Trigger shot toggle — contextual, only for hCG meds */}
        {showTriggerToggle && !isTriggerShotContext ? (
          <View
            className="flex-row items-center justify-between"
            style={{
              paddingVertical: 10,
              paddingHorizontal: 14,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: isTriggerShot
                ? "rgba(234, 179, 8, 0.3)"
                : EMBER.colors.border,
              backgroundColor: isTriggerShot
                ? "rgba(234, 179, 8, 0.06)"
                : "transparent",
              marginBottom: 16,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 14, color: EMBER.colors.text }}
            >
              {t("add_medication.is_trigger_shot")}
            </Text>
            <MoonToggle value={isTriggerShot} onChange={setIsTriggerShot} />
          </View>
        ) : null}

        {/* Inline dosage: [ amount |▼ unit ] */}
        <Text className="font-dm-sans" style={labelStyle}>
          Dosage
        </Text>
        <View style={{ position: "relative", zIndex: 10 }}>
          <View className="flex-row">
            <TextInput
              value={dosageAmount}
              onChangeText={(text) => {
                setDosageAmount(text);
                if (doseError) setDoseError(null);
              }}
              placeholder="e.g., 150"
              placeholderTextColor={EMBER.colors.textFaint}
              keyboardType="decimal-pad"
              style={{
                ...inputStyle,
                flex: 1,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                borderRightWidth: 0,
                ...(doseError ? { borderColor: "#E11D48" } : {}),
              }}
            />
            <Pressable
              ref={doseUnitTriggerRef}
              onPress={() => {
                capturePress(
                  pressLabel(t("add_medication.summary_dosage")),
                  "open_dose_unit_dropdown",
                );
                setShowRouteDropdown(false);
                setShowDoseUnitDropdown((current) => !current);
              }}
              accessibilityRole="button"
              accessibilityLabel={t("add_medication.summary_dosage")}
              accessibilityState={{ expanded: showDoseUnitDropdown }}
              className="flex-row items-center justify-center"
              style={{
                gap: 4,
                paddingHorizontal: 14,
                borderWidth: 1,
                borderColor: showDoseUnitDropdown
                  ? EMBER.colors.warm
                  : EMBER.colors.border,
                borderTopRightRadius: 12,
                borderBottomRightRadius: 12,
                backgroundColor: "rgba(0, 0, 0, 0.03)",
                minWidth: 70,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  fontWeight: "500",
                  color: dosageUnit
                    ? EMBER.colors.text
                    : EMBER.colors.textFaint,
                }}
              >
                {dosageUnit || "unit"}
              </Text>
              <ChevronDown size={14} color={EMBER.colors.textMid} />
            </Pressable>
          </View>

          <AnchoredSelectMenu
            visible={showDoseUnitDropdown}
            triggerRef={doseUnitTriggerRef}
            value={dosageUnit}
            items={doseUnitMenuItems}
            align="right"
            width={150}
            maxHeight={300}
            accentColor={EMBER.colors.warm}
            captureEscape={false}
            testID="medication-dose-unit-menu"
            onClose={dismissSelectionMenus}
            onSelect={(unit) => {
              setDosageUnit(unit);
              setShowDoseUnitDropdown(false);
            }}
          />
        </View>

        {doseError ? (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 12, color: "#E11D48", marginTop: 6 }}
          >
            {t(`add_medication.dose_error_${doseError}`)}
          </Text>
        ) : null}

        {/* Quick-select dosage presets for known medications */}
        {(() => {
          const presets = DOSAGE_PRESETS[name.toLowerCase()];
          if (!presets) return null;
          return (
            <View
              className="flex-row flex-wrap"
              style={{
                gap: 6,
                marginTop: 8,
              }}
            >
              {presets.doses.map((dose) => {
                const isSelected =
                  dosageAmount === String(dose) && dosageUnit === presets.unit;
                return (
                  <Pressable
                    key={dose}
                    onPress={() => {
                      capturePress(
                        pressLabel(`${dose} ${presets.unit}`),
                        "dose_preset",
                      );
                      setDosageAmount(String(dose));
                      setDosageUnit(presets.unit);
                    }}
                    style={{
                      paddingVertical: 6,
                      paddingHorizontal: 12,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: isSelected
                        ? EMBER.colors.warm
                        : EMBER.colors.border,
                      backgroundColor: isSelected
                        ? "rgba(139, 58, 26, 0.08)"
                        : "transparent",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        color: isSelected
                          ? EMBER.colors.warm
                          : EMBER.colors.textMid,
                        fontWeight: isSelected ? "600" : "400",
                      }}
                    >
                      {dose} {presets.unit}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          );
        })()}
        <View style={{ marginBottom: 16 }} />

        {/* Route — only show as separate section when no route is pre-filled */}
        {!route ? (
          <>
            <MoonFormField label={t("add_medication.route")}>
              <View className="flex-row flex-wrap" style={{ gap: 8 }}>
                {ROUTE_OPTIONS.map((opt) => {
                  const isSelected = route === opt.value;
                  const IconComp = opt.icon;
                  return (
                    <Pressable
                      key={opt.value}
                      onPress={() => {
                        capturePress(
                          pressLabel(t(opt.labelKey)),
                          "medication_route",
                        );
                        handleRouteChange(opt.value);
                      }}
                      className="flex-row items-center"
                      style={{
                        paddingVertical: 8,
                        paddingHorizontal: 14,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: isSelected
                          ? EMBER.colors.warm
                          : EMBER.colors.border,
                        backgroundColor: isSelected
                          ? "rgba(139, 58, 26, 0.08)"
                          : "transparent",
                        gap: 6,
                      }}
                    >
                      <IconComp
                        size={14}
                        color={
                          isSelected ? EMBER.colors.warm : EMBER.colors.textMid
                        }
                      />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          color: isSelected
                            ? EMBER.colors.warm
                            : EMBER.colors.textMid,
                        }}
                      >
                        {t(opt.labelKey)}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </MoonFormField>
            <View style={{ marginBottom: 16 }} />
          </>
        ) : null}

        {/* Delivery method — only for injectable routes */}
        {INJECTABLE_ROUTES.includes(route) ? (
          <>
            <MoonFormField label={t("add_medication.delivery_method")}>
              <View className="flex-row flex-wrap" style={{ gap: 8 }}>
                {INJECTION_DELIVERY_METHODS.map((opt) => {
                  const isSelected = deliveryMethod === opt.value;
                  return (
                    <Pressable
                      key={opt.value}
                      onPress={() => {
                        capturePress(
                          pressLabel(t(opt.labelKey)),
                          "delivery_method",
                        );
                        (() =>
                          setDeliveryMethod(isSelected ? "" : opt.value))();
                      }}
                      className="flex-row items-center"
                      style={{
                        paddingVertical: 8,
                        paddingHorizontal: 14,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: isSelected
                          ? EMBER.colors.warm
                          : EMBER.colors.border,
                        backgroundColor: isSelected
                          ? "rgba(139, 58, 26, 0.08)"
                          : "transparent",
                        gap: 6,
                      }}
                    >
                      <Syringe
                        size={14}
                        color={
                          isSelected ? EMBER.colors.warm : EMBER.colors.textMid
                        }
                      />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          color: isSelected
                            ? EMBER.colors.warm
                            : EMBER.colors.textMid,
                        }}
                      >
                        {t(opt.labelKey)}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </MoonFormField>
            <View style={{ marginBottom: 16 }} />
          </>
        ) : null}

        {inventoryMode ? (
          <>
            <MoonFormField
              label={t("add_medication.inventory.stock_on_hand_label")}
              style={{ marginBottom: 10 }}
            >
              <View className="flex-row" style={{ gap: 8 }}>
                <TextInput
                  value={inventoryQuantity}
                  onChangeText={setInventoryQuantity}
                  placeholder="1"
                  placeholderTextColor={EMBER.colors.textFaint}
                  keyboardType="number-pad"
                  style={{ ...inputStyle, flex: 0.72 }}
                />
                <TextInput
                  value={inventoryUnitLabel}
                  onChangeText={setInventoryUnitLabel}
                  placeholder={inferredInventoryUnit}
                  placeholderTextColor={EMBER.colors.textFaint}
                  style={{ ...inputStyle, flex: 1 }}
                />
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: EMBER.colors.textLight,
                  marginTop: 8,
                  lineHeight: 17,
                }}
              >
                {t("add_medication.inventory.stock_on_hand_helper")}
              </Text>
            </MoonFormField>

            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(
                    t(
                      includeTakingSchedule
                        ? "add_medication.inventory.toggle_hide_schedule"
                        : "add_medication.inventory.toggle_add_schedule",
                    ),
                  ),
                  "toggle_taking_schedule",
                );
                setIncludeTakingSchedule((value) => !value);
              }}
              className="flex-row items-center justify-between"
              style={{
                paddingVertical: 12,
                paddingHorizontal: 14,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: EMBER.colors.border,
                backgroundColor: includeTakingSchedule
                  ? "rgba(139, 58, 26, 0.08)"
                  : "transparent",
                marginBottom: 16,
              }}
            >
              <View style={{ flex: 1, paddingRight: 12 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                  }}
                >
                  {includeTakingSchedule
                    ? t("add_medication.inventory.toggle_hide_schedule")
                    : t("add_medication.inventory.toggle_add_schedule")}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    color: EMBER.colors.textLight,
                    marginTop: 3,
                  }}
                >
                  {t("add_medication.inventory.toggle_schedule_helper")}
                </Text>
              </View>
              <MoonToggle
                value={includeTakingSchedule}
                onChange={setIncludeTakingSchedule}
              />
            </Pressable>
          </>
        ) : null}

        {showScheduleFields ? (
          <>
            {/* Start date */}
            <View style={{ marginBottom: 16 }}>
              <MoonDateField
                label={t("add_medication.start_date")}
                value={startDate}
                onChange={setStartDate}
              />
            </View>

            {/* Duration chips */}
            <MoonFormField label={t("add_medication.duration")}>
              <View className="flex-row flex-wrap" style={{ gap: 8 }}>
                {DURATION_PRESETS.map((preset) => {
                  const isSelected = durationPreset === preset.labelKey;
                  return (
                    <Pressable
                      key={preset.labelKey}
                      onPress={() => {
                        capturePress(
                          pressLabel(t(preset.labelKey)),
                          "duration_preset",
                        );
                        (() =>
                          setDurationPreset(
                            isSelected ? null : preset.labelKey,
                          ))();
                      }}
                      style={{
                        paddingVertical: 8,
                        paddingHorizontal: 14,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: isSelected
                          ? EMBER.colors.warm
                          : EMBER.colors.border,
                        backgroundColor: isSelected
                          ? "rgba(139, 58, 26, 0.08)"
                          : "transparent",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          color: isSelected
                            ? EMBER.colors.warm
                            : EMBER.colors.textMid,
                        }}
                      >
                        {t(preset.labelKey)}
                      </Text>
                    </Pressable>
                  );
                })}
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(t("add_medication.custom")),
                      "add_medication.custom",
                    );
                    (() =>
                      setDurationPreset(
                        durationPreset === "Custom" ? null : "Custom",
                      ))();
                  }}
                  style={{
                    paddingVertical: 8,
                    paddingHorizontal: 14,
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor:
                      durationPreset === "Custom"
                        ? EMBER.colors.warm
                        : EMBER.colors.border,
                    backgroundColor:
                      durationPreset === "Custom"
                        ? "rgba(139, 58, 26, 0.08)"
                        : "transparent",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color:
                        durationPreset === "Custom"
                          ? EMBER.colors.warm
                          : EMBER.colors.textMid,
                    }}
                  >
                    {t("add_medication.custom")}
                  </Text>
                </Pressable>
              </View>
            </MoonFormField>
            {durationPreset === "Custom" && (
              <View className="flex-row" style={{ gap: 8, marginTop: 10 }}>
                <TextInput
                  value={customDurationValue}
                  onChangeText={setCustomDurationValue}
                  placeholder={t("add_medication.custom_duration_placeholder")}
                  placeholderTextColor={EMBER.colors.textFaint}
                  keyboardType="number-pad"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <View className="flex-row" style={{ gap: 6 }}>
                  {(
                    [
                      {
                        key: "days",
                        labelKey: "add_medication.duration_unit_days",
                      },
                      {
                        key: "weeks",
                        labelKey: "add_medication.duration_unit_weeks",
                      },
                      {
                        key: "months",
                        labelKey: "add_medication.duration_unit_months",
                      },
                    ] as const
                  ).map(({ key: unit, labelKey: unitLabelKey }) => (
                    <Pressable
                      key={unit}
                      onPress={() => {
                        capturePress(
                          pressLabel(t(unitLabelKey)),
                          "custom_duration_unit",
                        );
                        setCustomDurationUnit(unit);
                      }}
                      style={{
                        paddingVertical: 8,
                        paddingHorizontal: 10,
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor:
                          customDurationUnit === unit
                            ? EMBER.colors.warm
                            : EMBER.colors.border,
                        backgroundColor:
                          customDurationUnit === unit
                            ? "rgba(139, 58, 26, 0.08)"
                            : "transparent",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          color:
                            customDurationUnit === unit
                              ? EMBER.colors.warm
                              : EMBER.colors.textMid,
                        }}
                      >
                        {t(unitLabelKey)}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            )}
            {/* In edit mode a finite course arrives as a bare endDate with no
                duration preset — the date must stay visible or the preserved
                course reads as "Ongoing" (#2218). */}
            {computedEndDate &&
            (editMedication != null ||
              (durationPreset &&
                durationPreset !==
                  "add_medication.duration_preset.ongoing")) ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: EMBER.colors.textLight,
                  marginTop: 6,
                }}
              >
                Ends {formatDate(computedEndDate)}
              </Text>
            ) : null}
            <View style={{ marginBottom: 16 }} />

            {/* Time of day + custom time entry */}
            <MoonFormField label={t("add_medication.time_of_day")}>
              <View className="flex-row flex-wrap" style={{ gap: 8 }}>
                {TIME_PRESETS.map((preset) => (
                  <MoonFilterChip
                    key={preset.value}
                    label={`${t(preset.labelKey)} (${preset.value})`}
                    icon={preset.icon}
                    selected={selectedTimes.includes(preset.value)}
                    onPress={() => handleTimeToggle(preset.value)}
                    radius={10}
                    showCheck={false}
                  />
                ))}
                {/* Custom time chip */}
                <MoonFilterChip
                  label={t("add_medication.custom")}
                  icon={Clock}
                  selected={showCustomTimeInput}
                  onPress={() => setShowCustomTimeInput(!showCustomTimeInput)}
                  radius={10}
                  showCheck={false}
                />
              </View>
            </MoonFormField>

            {/* Custom time input */}
            {showCustomTimeInput && (
              <View
                className="flex-row items-center"
                style={{
                  gap: 8,
                  marginTop: 10,
                }}
              >
                <TextInput
                  value={customTimeValue}
                  onChangeText={setCustomTimeValue}
                  placeholder={t("add_medication.custom_time_placeholder")}
                  placeholderTextColor={EMBER.colors.textFaint}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(t("add_medication.add")),
                      "add_medication.add",
                    );
                    const trimmed = customTimeValue.trim();
                    if (
                      isValidTime(trimmed) &&
                      !selectedTimes.includes(trimmed)
                    ) {
                      setSelectedTimes((prev) => [...prev, trimmed]);
                      setCustomTimeValue("");
                    }
                  }}
                  disabled={!isValidTime(customTimeValue.trim())}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 14,
                    borderRadius: 10,
                    backgroundColor: isValidTime(customTimeValue.trim())
                      ? EMBER.colors.warm
                      : "rgba(0,0,0,0.06)",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      fontWeight: "600",
                      color: isValidTime(customTimeValue.trim())
                        ? "#FFFFFF"
                        : EMBER.colors.textFaint,
                    }}
                  >
                    {t("add_medication.add")}
                  </Text>
                </Pressable>
              </View>
            )}

            {/* Custom time tags (removable) */}
            {selectedTimes.filter(
              (t) => !TIME_PRESETS.some((p) => p.value === t),
            ).length > 0 && (
              <View
                className="flex-row flex-wrap"
                style={{
                  gap: 6,
                  marginTop: 8,
                }}
              >
                {selectedTimes
                  .filter((t) => !TIME_PRESETS.some((p) => p.value === t))
                  .map((t) => (
                    <Pressable
                      key={t}
                      onPress={() => {
                        capturePress(pressLabel(t), "remove_dose_time");
                        (() =>
                          setSelectedTimes((prev) =>
                            prev.filter((x) => x !== t),
                          ))();
                      }}
                      className="flex-row items-center"
                      style={{
                        gap: 4,
                        paddingVertical: 4,
                        paddingHorizontal: 10,
                        borderRadius: 8,
                        backgroundColor: "rgba(139, 58, 26, 0.08)",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{ fontSize: 12, color: EMBER.colors.warm }}
                      >
                        {t}
                      </Text>
                      <Minus size={12} color={EMBER.colors.warm} />
                    </Pressable>
                  ))}
              </View>
            )}

            {/* Multi-dose summary badge */}
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textLight,
                marginTop: 6,
                marginBottom: 16,
              }}
            >
              {formatTimeSummary(selectedTimes, t)}
            </Text>

            {/* Notes */}
            <MoonFormField
              label={t("add_medication.notes_optional")}
              style={{ marginBottom: 16 }}
            >
              <TextInput
                value={notes}
                onChangeText={setNotes}
                placeholder={t("add_medication.notes_placeholder")}
                placeholderTextColor={EMBER.colors.textFaint}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
                style={{ ...inputStyle, minHeight: 72 }}
              />
            </MoonFormField>

            {/* Link to cycle toggle */}
            {cycleId ? (
              <View
                className="flex-row items-center justify-between"
                style={{
                  paddingVertical: 12,
                  marginBottom: 16,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 14, color: EMBER.colors.text }}
                >
                  {t("add_medication.link_to_cycle")}
                </Text>
                <MoonToggle value={linkToCycle} onChange={setLinkToCycle} />
              </View>
            ) : null}
          </>
        ) : null}

        <DialogPrimaryButton
          label={t("add_medication.review")}
          onPress={() => {
            if (name.trim()) setStep(3);
          }}
          disabled={!name.trim()}
          style={{ marginBottom: 32 }}
        />
      </ScrollView>
    );
  }

  // ============================================================================
  // Step 3 — Review & Confirm
  // ============================================================================

  function renderStep3() {
    const isSubmitting =
      createMedication.isPending || updateMedication.isPending;
    const showScheduleFields = !inventoryMode || includeTakingSchedule;
    const inventoryEntry = inventoryMode ? buildInventoryEntry() : null;
    const submitLabel = isEditMode
      ? t("add_medication.submit_edit")
      : inventoryMode && includeTakingSchedule
        ? t("add_medication.inventory.submit_add_with_schedule")
        : inventoryMode
          ? t("add_medication.inventory.submit_add_only")
          : effectiveIsTriggerShot
            ? t("add_medication.submit_add_trigger")
            : t("add_medication.submit_add");

    const summaryRows: { label: string; value: string }[] = [
      { label: t("add_medication.medication_name"), value: name },
    ];
    if (effectiveIsTriggerShot) {
      summaryRows.push({
        label: t("add_medication.summary_role"),
        value: t("add_medication.summary_role_trigger_shot"),
      });
    }
    if (dosageAmount) {
      const unitDisplay = dosageUnit
        ? ` ${DOSE_UNIT_KEYS[dosageUnit] ? t(DOSE_UNIT_KEYS[dosageUnit]) : dosageUnit}`
        : "";
      summaryRows.push({
        label: t("add_medication.summary_dosage"),
        value: `${dosageAmount}${unitDisplay}`,
      });
    }
    if (route) {
      const routeOpt = ROUTE_OPTIONS.find((o) => o.value === route);
      summaryRows.push({
        label: t("add_medication.summary_route"),
        value: routeOpt ? t(routeOpt.labelKey) : route,
      });
    }
    if (deliveryMethod) {
      const dmOpt = INJECTION_DELIVERY_METHODS.find(
        (m) => m.value === deliveryMethod,
      );
      if (dmOpt) {
        summaryRows.push({
          label: t("add_medication.summary_delivery_method"),
          value: t(dmOpt.labelKey),
        });
      }
    }
    if (inventoryEntry) {
      summaryRows.push({
        label: t("add_medication.inventory.stock_on_hand_label"),
        value: `${inventoryEntry.quantity} ${inventoryEntry.unitLabel}`,
      });
    }
    if (showScheduleFields) {
      summaryRows.push({
        label: t("add_medication.summary_start_date"),
        value: formatDate(startDate),
      });
    }
    if (showScheduleFields && durationPreset) {
      const durPreset = DURATION_PRESETS.find(
        (p) => p.labelKey === durationPreset,
      );
      summaryRows.push({
        label: t("add_medication.summary_duration"),
        value:
          durationPreset === "Custom"
            ? `${customDurationValue} ${customDurationUnit}`
            : durPreset
              ? t(durPreset.labelKey)
              : durationPreset,
      });
    }
    if (showScheduleFields && computedEndDate) {
      summaryRows.push({
        label: t("add_medication.summary_end_date"),
        value: formatDate(computedEndDate),
      });
    }
    if (showScheduleFields) {
      summaryRows.push({
        label: t("add_medication.summary_schedule"),
        value: formatTimeSummary(selectedTimes, t),
      });
    }
    if (showScheduleFields && notes) {
      summaryRows.push({
        label: t("add_medication.summary_notes"),
        value: notes,
      });
    }
    if (showScheduleFields && cycleId && linkToCycle) {
      summaryRows.push({
        label: t("add_medication.summary_linked_to_cycle"),
        value: t("add_medication.summary_yes"),
      });
    }

    return (
      <ScrollView style={{ padding: 24 }} showsVerticalScrollIndicator={false}>
        <StepDots current={3} total={4} />

        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textMid,
            textAlign: "center",
            marginBottom: 20,
          }}
        >
          {effectiveIsTriggerShot
            ? t("add_medication.trigger_review_intro")
            : t("add_medication.review_intro")}
        </Text>

        {/* Summary card */}
        <View
          style={{
            borderRadius: 14,
            borderWidth: 1,
            borderColor: EMBER.colors.border,
            padding: 16,
            marginBottom: 24,
          }}
        >
          {summaryRows.map((row, i) => (
            <View
              key={row.label}
              className="flex-row justify-between"
              style={{
                alignItems: "flex-start",
                paddingVertical: 10,
                ...(i < summaryRows.length - 1 ? { borderBottomWidth: 0 } : {}),
              }}
            >
              <Text
                className="font-dm-sans"
                style={{ fontSize: 13, color: EMBER.colors.textLight, flex: 1 }}
              >
                {row.label}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  color: EMBER.colors.text,
                  fontWeight: "500",
                  flex: 2,
                  textAlign: "right",
                }}
              >
                {row.value}
              </Text>
            </View>
          ))}
        </View>

        <View style={{ gap: 10, marginBottom: 32 }}>
          <DialogPrimaryButton
            label={submitLabel}
            onPress={handleSubmit}
            loading={isSubmitting}
          />

          {!isEditMode && !inventoryMode && !effectiveIsTriggerShot ? (
            <View>
              <SoftSecondaryButton
                label={t("add_medication.save_add_different_dose")}
                onPress={handleSubmitAndAddAnother}
                disabled={isSubmitting}
              />
              <Text
                className="font-dm-sans"
                style={{
                  marginTop: 4,
                  paddingHorizontal: 12,
                  fontSize: 12,
                  color: EMBER.colors.textLight,
                  textAlign: "center",
                }}
              >
                {t("add_medication.save_add_different_dose_help")}
              </Text>
            </View>
          ) : null}
        </View>
      </ScrollView>
    );
  }

  // ============================================================================
  // Header
  // ============================================================================

  const stepTitles: Record<Step, string> = {
    0: isEditMode
      ? t("add_medication.step_title_edit")
      : inventoryMode
        ? t("add_medication.inventory.step_title_add")
        : isTriggerShotContext
          ? t("add_medication.trigger_step_title_add")
          : t("add_medication.step_title_add"),
    1: isTriggerShotContext
      ? t("add_medication.trigger_step_title_search")
      : t("add_medication.step_title_search"),
    2: isEditMode
      ? t("add_medication.step_title_edit")
      : inventoryMode
        ? t("add_medication.inventory.step_title_configure")
        : isTriggerShotContext
          ? t("add_medication.trigger_step_title_configure")
          : t("add_medication.step_title_configure"),
    3: inventoryMode
      ? t("add_medication.inventory.step_title_review")
      : isTriggerShotContext
        ? t("add_medication.trigger_step_title_review")
        : t("add_medication.step_title_review"),
  };

  const canGoBack = isEditMode ? false : step > 0;
  const dialogMaxHeight = windowHeight * 0.85;
  // Keep the shared Dialog surface unchanged while giving this long,
  // form-heavy dialog a light content wash like the layered Care detail
  // dialogs. The wash is intentionally not another backdrop-filter layer:
  // that would create a stacking context and interfere with anchored menus
  // that must render above the dialog.
  const medicationContentSurfaceStyle = {
    backgroundColor: "rgba(255,255,255,0.18)",
  };

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="minimal"
      containerStyle={{
        width: "90%",
        maxWidth: 420,
        maxHeight: dialogMaxHeight,
      }}
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        maxHeight: dialogMaxHeight,
        ...medicationContentSurfaceStyle,
        paddingTop: 0,
        paddingHorizontal: 0,
        paddingBottom: 0,
      }}
      testID="medication-dialog"
    >
      <View
        testID="medication-dialog-header"
        style={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: 52,
          paddingHorizontal: 52,
          paddingTop: 10,
        }}
      >
        {canGoBack ? (
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("add_medication.back")),
                "add_medication.back",
              );
              setStep((step - 1) as Step);
            }}
            accessibilityRole="button"
            accessibilityLabel={t("add_medication.back")}
            hitSlop={12}
            style={{
              position: "absolute",
              left: 20,
              top: 19,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 14, color: EMBER.colors.textLight }}
            >
              {t("add_medication.back")}
            </Text>
          </Pressable>
        ) : null}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 17,
            fontWeight: "700",
            color: EMBER.colors.text,
            textAlign: "center",
          }}
        >
          {stepTitles[step]}
        </Text>
      </View>

      {step === 0 && renderStep0()}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </Dialog>
  );
}
